import React, { useCallback, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import ManagerIcon from "@mui/icons-material/ManageAccounts";
import StoreIcon from "@mui/icons-material/Storefront";
import QuestionsIcon from "@mui/icons-material/QuestionMarkOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import ContactIcon from "@mui/icons-material/ContactsOutlined"
import PricingIcon from "@mui/icons-material/SellOutlined";
import DemoIcon from "@mui/icons-material/FactCheckOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROLES } from "src/constants/roles";
import StorageService from "src/storage";
import { logout } from "src/redux/user/actions";
import { MixTheme } from "@styles";

const drawerWidth = 260;

const AdminDrawer = () => {
  const theme = useTheme<MixTheme>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.user);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const menuItems = useMemo(
    () => [
      { title: "Home", icon: <HomeIcon />, to: "/", admin: false, category: "System" },
      { title: "Companies", icon: <StoreIcon />, to: "/companies", admin: false, category: "System" },
      { title: "Contacts", icon: <ContactIcon />, to: "/contacts", admin: false, category: "System" },
      { title: "Free Demo Requests", icon: <DemoIcon />, to: "/free-demo-requests", admin: false, category: "System" },
      { title: "Plans & Pricing", icon: <PricingIcon />, to: "/plans", admin: false, category: "Configuration" },
      { title: "Leads", icon: <ManagerIcon />, to: "/lead", admin: true, category: "Configuration" },
      { title: "Questions", icon: <QuestionsIcon />, to: "/questions", admin: true, category: "Configuration" },
      { title: "Settings", icon: <SettingsIcon />, to: "/app-settings", admin: false, category: "Configuration" },
      { title: "Change Password", icon: <LockIcon />, to: "/changePassword", admin: false, category: "Configuration" },
    ],
    []
  );

  const activePath = location.pathname;

  const isActiveRoute = useCallback(
    (route: string) =>
      route === "/" ? activePath === "/" : activePath.startsWith(route),
    [activePath]
  );

  const activeTitle = useMemo(() => {
    return menuItems.find((item) => isActiveRoute(item.to))?.title || "";
  }, [menuItems, isActiveRoute]);

  const confirmLogout = useCallback(async () => {
    setLogoutOpen(false);
    StorageService.token.clearAll();
    await dispatch(logout());
    navigate("/");
  }, [dispatch, navigate]);

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Brand */}
      <Box sx={{ px: 3, py: 2.5 }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <img src="/logo.png" alt="TYFHU logo" style={{ width: 28, height: 28 }} />
          <Typography
            fontSize="20px"
            fontWeight={800}
            letterSpacing="-0.4px"
            color="primary.main"
          >
            TYFHU
          </Typography>
        </Box>
        <Typography fontSize="12px" color={theme.palette.text.secondary} sx={{ mt: 0.5 }}>
          Admin Console
        </Typography>
      </Box>

      <Divider />

      {/* Navigation */}
      <Box sx={{ px: 1, py: 1, flexGrow: 1, overflowY: "auto" }}>
        {(["System", "Configuration"] as const).map((cat) => {
          const catItems = menuItems.filter(
            (item) => item.category === cat && (!item.admin || user.role === ROLES.ADMIN)
          );

          if (catItems.length === 0) return null;

          return (
            <Box key={cat} sx={{ mb: 2 }}>
              <Typography
                fontSize="10px"
                fontWeight={700}
                color="text.disabled"
                sx={{
                  px: 2,
                  py: 0.5,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                {cat}
              </Typography>
              <List disablePadding>
                {catItems.map((item) => {
                  const active = isActiveRoute(item.to);

                  return (
                    <ListItem key={item.title} disablePadding sx={{ mb: "4px" }}>
                      <ListItemButton
                        onClick={() => {
                          navigate(item.to);
                          setMobileOpen(false);
                        }}
                        sx={{
                          borderRadius: "10px",
                          px: 2,
                          py: 1,
                          backgroundColor: active
                            ? theme.palette.primary.main + "14"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main + "0F",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: active
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: "13px",
                            fontWeight: active ? 600 : 500,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* Logout */}
      <Box sx={{ px: 2, pb: 2 }}>
        <ListItemButton
          onClick={() => setLogoutOpen(true)}
          sx={{
            borderRadius: "10px",
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.main + "10",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Top Bar */}
      <AppBar
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: "#fff",
          borderBottom: "1px solid #eee",
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ display: { sm: "none" }, mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            fontSize="16px"
            fontWeight={600}
            color={theme.palette.text.primary}
          >
            {activeTitle}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            borderRight: "1px solid #eee",
            backgroundColor: "#fafafa",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: drawerWidth },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: "64px",
          px: 3,
          py: 3,
          backgroundColor: "#f9fafb",
          minHeight: "100vh",
        }}
      >
        <Outlet />
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <DialogTitle>Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to log out of the admin console?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmLogout}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDrawer;
